// Cookie Management Utility
// DSGVO-konform und EAA 2025 kompatibel

export const COOKIE_CATEGORIES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
  FUNCTIONAL: 'functional'
};

export const COOKIE_CONSENT_KEY = 'cookie-consent';
export const COOKIE_CONSENT_DATE_KEY = 'cookie-consent-date';

// Check if user has given consent for a specific category
export const hasConsent = (category) => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) return false;
    
    const preferences = JSON.parse(consent);
    return preferences[category] === true;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return false;
  }
};

// Get all consent preferences
export const getConsentPreferences = () => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) return null;
    
    return JSON.parse(consent);
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
};

// Set consent preferences
export const setConsentPreferences = (preferences) => {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
    localStorage.setItem(COOKIE_CONSENT_DATE_KEY, new Date().toISOString());
    
    // Trigger custom event for other parts of the app to listen to
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
      detail: preferences
    }));
    
    return true;
  } catch (error) {
    console.error('Error setting cookie consent:', error);
    return false;
  }
};

// Clear all consent data
export const clearConsent = () => {
  try {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_CONSENT_DATE_KEY);
    
    // Clear all non-necessary cookies
    clearNonNecessaryCookies();
    
    window.dispatchEvent(new CustomEvent('cookieConsentCleared'));
    return true;
  } catch (error) {
    console.error('Error clearing cookie consent:', error);
    return false;
  }
};

// Clear all non-necessary cookies
export const clearNonNecessaryCookies = () => {
  // Get all cookies
  const cookies = document.cookie.split(';');
  
  // List of necessary cookies that should not be deleted
  const necessaryCookies = [
    COOKIE_CONSENT_KEY,
    'session',
    'csrf',
    'auth'
  ];
  
  cookies.forEach(cookie => {
    const [name] = cookie.split('=');
    const cookieName = name.trim();
    
    // Don't delete necessary cookies
    if (!necessaryCookies.some(necessary => cookieName.includes(necessary))) {
      // Delete cookie by setting expiry date in the past
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
    }
  });
};

// Check if consent is still valid (not older than 13 months as per DSGVO)
export const isConsentValid = () => {
  try {
    const consentDate = localStorage.getItem(COOKIE_CONSENT_DATE_KEY);
    if (!consentDate) return false;
    
    const consentTimestamp = new Date(consentDate).getTime();
    const now = new Date().getTime();
    const thirteenMonths = 13 * 30 * 24 * 60 * 60 * 1000; // 13 months in milliseconds
    
    return (now - consentTimestamp) < thirteenMonths;
  } catch (error) {
    console.error('Error checking consent validity:', error);
    return false;
  }
};

// Load scripts based on consent
export const loadConsentBasedScripts = (preferences) => {
  // Analytics Scripts (e.g., Google Analytics)
  if (preferences.analytics) {
    loadAnalyticsScripts();
  }
  
  // Marketing Scripts (e.g., Facebook Pixel, Google Ads)
  if (preferences.marketing) {
    loadMarketingScripts();
  }
  
  // Functional Scripts (e.g., Chat widgets, Maps)
  if (preferences.functional) {
    loadFunctionalScripts();
  }
};

// Load analytics scripts
const loadAnalyticsScripts = () => {
  // Example: Google Analytics 4
  if (!window.gtag && !document.querySelector('script[src*="googletagmanager"]')) {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID';
    document.head.appendChild(script);
    
    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'GA_MEASUREMENT_ID', {
        anonymize_ip: true,
        cookie_flags: 'SameSite=Strict;Secure'
      });
    };
  }
};

// Load marketing scripts
const loadMarketingScripts = () => {
  // Example: Facebook Pixel
  if (!window.fbq) {
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', 'YOUR_PIXEL_ID');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
  }
};

// Load functional scripts
const loadFunctionalScripts = () => {
  // Example: Load chat widget or other functional scripts
  console.log('Loading functional scripts');
};

// Initialize cookie manager
export const initializeCookieManager = () => {
  // Check if consent exists and is valid
  const preferences = getConsentPreferences();
  const isValid = isConsentValid();
  
  if (preferences && isValid) {
    // Load scripts based on existing consent
    loadConsentBasedScripts(preferences);
  } else if (preferences && !isValid) {
    // Consent expired, clear it
    clearConsent();
  }
  
  // Listen for consent changes
  window.addEventListener('cookieConsentChanged', (event) => {
    loadConsentBasedScripts(event.detail);
  });
  
  window.addEventListener('cookieConsentCleared', () => {
    // Reload page to ensure all scripts are properly removed
    // window.location.reload();
  });
};
